import * as React from 'react'
import {
  Star,
  Trash,
  PencilSimple,
  X,
  WarningCircle,
} from '@phosphor-icons/react'
import {
  ProficiencyDots,
  useConfirmationDialogContext,
} from 'src/design-system'
import { ProfileSkillData } from './types'
import { SkillActivityFeed } from 'components/updates-drawer/skill-activity-feed'
import { store } from 'store/index'
import styles from '../../styles/scrollbar-hide.module.css'

type SkillViewProps = {
  skill: ProfileSkillData
  onClose: () => void
  onDelete?: (skillId: string) => void
  onEdit?: (skillId: string) => void
  onDeclare?: (skillId: string) => void
  onToggleStar?: (skillId: string, starred: boolean) => void
}

export const SkillView: React.FC<SkillViewProps> = ({
  skill,
  onClose,
  onDelete,
  onEdit,
  onDeclare,
  onToggleStar,
}) => {
  const { openConfirmationDialog } = useConfirmationDialogContext()
  const isInferred = skill.source === 'implied'
  const isStarred = skill.starred === true
  const skillTypeLabel = skill.skillType
    ? skill.skillType.charAt(0).toUpperCase() +
      skill.skillType.slice(1) +
      ' skill'
    : null

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    openConfirmationDialog('delete-profile-skill', {
      title: 'Are you sure you want to delete this skill?',
      body: `This action cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: () => {
        onDelete?.(skill.id)
      },
    })
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <h2 className="text-lg font-bold text-gray-900 mb-0 truncate">
            {skill.name}
          </h2>
          <button
            type="button"
            onClick={() => onToggleStar?.(skill.id, !isStarred)}
            className="p-1 rounded transition-colors"
            title={isStarred ? 'Un-star the skill' : 'Star the skill'}
          >
            <Star
              weight={isStarred ? 'fill' : 'regular'}
              className={`w-5 h-5 ${
                isStarred ? 'text-theme-50' : 'text-gray-400'
              }`}
            />
          </button>
          {isInferred && (
            <span className="inline-flex items-center h-[20px] text-xs text-orange-700 bg-orange-100 px-2 py-0.5 rounded flex-shrink-0">
              Inferred
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {onDelete && (
            <button
              type="button"
              onClick={handleDelete}
              className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
              title="Delete skill"
            >
              <Trash className="w-5 h-5 text-gray-600" />
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(skill.id)}
              className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
              title="Edit skill"
            >
              <PencilSimple className="w-5 h-5 text-gray-600" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-[#e7eaf4] rounded-full transition-colors"
            title="Close"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      <div
        className={`flex-1 overflow-y-auto px-6 py-6 ${styles.hideScrollbar}`}
      >
        {isInferred && onDeclare && (
          <div className="bg-[#FFF0E5] rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <WarningCircle className="w-5 h-5 text-[#E42309] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-sm text-[#E42309] mb-5">
                  This skill has been inferred based on your current role.
                  Please declair or update the proficiency to accurately reflect
                  your expertise. Delete it if it is not applicable.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => onDeclare?.(skill.id)}
                    className="px-4 py-1.5 text-sm font-medium text-white bg-[#1D264A] rounded-[4px] hover:bg-[#5E6580]"
                  >
                    Declare Skill
                  </button>
                  <button
                    onClick={() => onEdit?.(skill.id)}
                    className="px-4 py-1.5 text-sm font-medium text-[#1D264A] bg-[#FFF0E5] rounded-[4px] hover:text-[#5E6580]"
                    style={{ border: '1px solid #1D264A' }}
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {skill.description && (
          <p className="text-sm text-gray-900 leading-relaxed mb-6">
            {skill.description}
          </p>
        )}

        <div className="space-y-5 mb-7">
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-900 w-32">
              Skill proficiency
            </span>
            <ProficiencyDots
              level={skill.proficiencyLevel ?? 0}
              filledColor={isInferred ? 'orange' : 'theme'}
            />
          </div>

          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-900 w-32">
              Skill type
            </span>
            <span className="text-sm text-gray-600">
              {skillTypeLabel || '-'}
            </span>
          </div>
        </div>

        <div className="border-t border-gray-200 ">
          <h3 className="font-medium text-sm mb-4">Activity</h3>
          <SkillActivityFeed
            skillId={String(skill.profileSkillId || skill.id)}
            userId={store.nonNullCurrentUser.id}
          />
        </div>
      </div>
    </div>
  )
}
